import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Globe, Building2, Users, Target, TrendingUp } from 'lucide-react';
import { AnimatedGradient } from '../components/ui/animated-gradient-with-svg';
import Lenis from '@studio-freight/lenis';
import { Footer } from '../components/Footer';

// Word-by-word animated quote component - Centered
const AnimatedQuote = ({ text, isVisible, textColor }) => {
  const words = text.split(' ');

  return (
    <p className="text-center max-w-[800px] mx-auto" style={{ perspective: '1000px' }}>
      {words.map((word, idx) => (
        <span
          key={idx}
          className="inline-block"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            color: textColor,
            fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
            lineHeight: 1.7,
            fontStyle: 'italic',
            marginRight: '0.35em',
            opacity: isVisible ? 1 : 0,
            transform: isVisible
              ? 'translateY(0) rotateX(0deg)'
              : 'translateY(20px) rotateX(90deg)',
            transition: `opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${idx * 0.05}s, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${idx * 0.05}s`,
          }}
        >
          {word}
        </span>
      ))}
    </p>
  );
};

// AnimatedHeroTitle component removed

export const Home = () => {
  const { language } = useLanguage();
  const homeWrapperRef = useRef(null);
  const testimonialSectionRef = useRef(null);
  const teamSectionRef = useRef(null);
  const scrollContentRef = useRef(null);

  const [textColor, setTextColor] = useState('#0a1f35');
  const [isLightBg, setIsLightBg] = useState(true);
  const [heroVisible, setHeroVisible] = useState(false);
  const [bgColor, setBgColor] = useState('#f4f5f7');
  
  // Testimonial carousel state - simple index-based
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isInTestimonialSection, setIsInTestimonialSection] = useState(false);
  const [carouselDone, setCarouselDone] = useState(false);
  const [quoteAnimationComplete, setQuoteAnimationComplete] = useState(false);
  const autoAdvanceTimer = useRef(null);

  // Initialize Lenis smooth scrolling
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Expo ease out
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
      overscroll: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  // Auto-advance carousel every 3 seconds when in testimonial section
  useEffect(() => {
    if (isInTestimonialSection && !carouselDone) {
      autoAdvanceTimer.current = setInterval(() => {
        setCurrentTestimonial(prev => {
          if (prev < 2) {
            return prev + 1;
          } else {
            return 0;
          }
        });
      }, 5000);
    }
    
    return () => {
      if (autoAdvanceTimer.current) {
        clearInterval(autoAdvanceTimer.current);
      }
    };
  }, [isInTestimonialSection, carouselDone]);

  // Hero animation trigger
  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);


  // Track quote animation completion
  useEffect(() => {
    if (isInTestimonialSection && !carouselDone) {
      setQuoteAnimationComplete(false);
      const timer = setTimeout(() => {
        setQuoteAnimationComplete(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentTestimonial, isInTestimonialSection, carouselDone]);

  // Intersection Observer for testimonial section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            setIsInTestimonialSection(true);
          } else {
            setIsInTestimonialSection(false);
          }
        });
      },
      { threshold: [0.5] }
    );

    if (testimonialSectionRef.current) {
      observer.observe(testimonialSectionRef.current);
    }

    return () => observer.disconnect();
  }, [carouselDone]);

  // Background color transition
  useEffect(() => {
    const interpolateColor = (color1, color2, factor) => {
      const c1 = color1.match(/\w\w/g).map(x => parseInt(x, 16));
      const c2 = color2.match(/\w\w/g).map(x => parseInt(x, 16));
      const result = c1.map((c, i) => Math.round(c + factor * (c2[i] - c)));
      return `#${result.map(x => x.toString(16).padStart(2, '0')).join('')}`;
    };

    const updateBackgroundColor = () => {
      if (!teamSectionRef.current || !scrollContentRef.current) return;
      
      const teamRect = teamSectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      const teamTop = teamRect.top;
      const transitionStart = viewportHeight;
      const transitionEnd = viewportHeight * 0.3;
      
      let transitionProgress = 0;
      
      if (teamTop <= transitionStart && teamTop >= transitionEnd) {
        transitionProgress = (transitionStart - teamTop) / (transitionStart - transitionEnd);
        transitionProgress = Math.max(0, Math.min(1, transitionProgress));
      } else if (teamTop < transitionEnd) {
        transitionProgress = 1;
      } else {
        transitionProgress = 0;
      }
      
      const newBgColor = interpolateColor('f4f5f7', '0a1f35', transitionProgress);
      setBgColor(newBgColor);
      
      if (transitionProgress > 0.4) {
        setTextColor('#f4f5f7');
        setIsLightBg(false);
      } else {
        setTextColor('#0a1f35');
        setIsLightBg(true);
      }
      
      if (scrollContentRef.current) {
        scrollContentRef.current.style.backgroundColor = newBgColor;
        scrollContentRef.current.style.transition = 'background-color 0.1s ease-out';
      }
    };

    window.addEventListener('scroll', updateBackgroundColor, { passive: true });
    updateBackgroundColor();

    return () => {
      window.removeEventListener('scroll', updateBackgroundColor);
    };
  }, []);

  // Updated metrics - Key Portfolio Metrics
  const metrics = [
    { value: '6', label: language === 'fr' ? 'Sociétés en portefeuille' : 'Portfolio companies', icon: Building2 },
    { value: '9', label: language === 'fr' ? 'Pays' : 'Countries', icon: Globe },
    { value: '4', label: language === 'fr' ? 'Secteurs cibles' : 'Target sectors', icon: Target },
    { value: '>150M', label: language === 'fr' ? 'Chiffre d\'affaires' : 'Revenue', icon: TrendingUp },
    { value: 'c800', label: language === 'fr' ? 'Employés' : 'Employees', icon: Users },
  ];

  const teamMembers = [
    {
      name: 'Guillaume Benhamou',
      title: 'Partner & Chief Executive Officer',
      image: '/Guillaume.png'
    },
    {
      name: 'Charles Bouygues',
      title: 'Partner & Chief Transformation Officer',
      image: '/Charles.png'
    },
    {
      name: 'Grégory Freva',
      title: 'Partner & Chief Operating Officer',
      image: '/Gregory.png'
    }
  ];

  // Updated testimonials - Order: GB, CB, GF (Guillaume first, then Charles, then Gregory)
  const testimonials = [
    {
      name: 'Guillaume Benhamou',
      title: 'Partner & Chief Executive Officer',
      initials: 'GB',
      image: '/guillaume homepage (1).png',
      quote: language === 'fr' 
        ? "\"Nous investissons là où la complexité et les enjeux sont élevés, pour l'entreprise, ses clients et ses fournisseurs. Et nous cherchons à construire des partenariats sur tous les aspects des entreprises dans lesquelles nous investissons.\""
        : "\"We invest where complexity and stakes are high, for the business, its customers, and its suppliers. And we aim to build partnerships across every dimension of the companies we back.\""
    },
    {
      name: 'Charles Bouygues',
      title: 'Partner & Chief Transformation Officer',
      initials: 'CB',
      image: 'https://customer-assets.emergentagent.com/job_dac2dc4e-4f19-40ab-966a-0ff854a140af/artifacts/xhg6gune_CHarles%20-.png',
      quote: language === 'fr'
        ? "\"La véritable excellence opérationnelle vient de la capacité à cibler rapidement le cœur des problèmes auxquels une entreprise est confrontée, mais aussi d'avoir la persistance d'aller à la cause racine, aussi profonde ou complexe soit-elle, pour construire des solutions durables.\""
        : "\"True operational excellence comes from the ability to quickly identify the core issues a business faces, but also from the persistence to reach the root cause, however deep or complex, and build lasting solutions.\""
    },
    {
      name: 'Grégory Freva',
      title: 'Partner & Chief Operating Officer',
      initials: 'GF',
      image: 'https://customer-assets.emergentagent.com/job_dac2dc4e-4f19-40ab-966a-0ff854a140af/artifacts/56gu8luw_Gregory%20-.png',
      quote: language === 'fr'
        ? "\"La transformation industrielle pour réussir nécessite une véritable proximité avec les problèmes sur le terrain. Nous nous rapprochons du problème, travaillons avec les équipes pour trouver des solutions, et combinons approches tactiques et stratégiques pour avancer vite mais avec une vision à long terme.\""
        : "\"Successful industrial transformation requires genuine proximity to the problems on the ground. We get close to the issue, work with the teams to find solutions, and combine tactical and strategic approaches to move fast while keeping the long view.\""
    }
  ];

  return (
    <div ref={homeWrapperRef} className="home-page-wrapper min-h-screen" style={{ overscrollBehavior: 'none' }}>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes lineEaseIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes wordBlurIn {
          0% { opacity: 0; filter: blur(12px); transform: translateY(20px); }
          100% { opacity: 1; filter: blur(0px); transform: translateY(0); }
        }
        
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(12px); opacity: 0.3; }
        }
        
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        
        @keyframes fakeScroll {
          0% { transform: translateY(0); opacity: 1; }
          30% { transform: translateY(16px); opacity: 0.6; }
          60% { transform: translateY(16px); opacity: 0; }
          61% { transform: translateY(0); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        
        .line-ease-animate {
          animation: lineEaseIn 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .word-blur-animate {
          animation: wordBlurIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
        }
      `}</style>

      {/* ============ FIXED HERO SECTION FOR PARALLAX ============ */}
      <div className="fixed inset-0 w-full h-screen z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundColor: '#0a1f35'
          }}
        >
          <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover">
            <source src="/New Opk.mp4" type="video/mp4" />
          </video>
          {/* Dark gradient overlay */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10, 31, 53, 0.4) 0%, rgba(10, 31, 53, 0.6) 100%)' }} />
        </div>

        <div className="relative z-10 h-full flex flex-col justify-center items-center px-6 pt-20 lg:pt-0 text-center">
          <div className="max-w-4xl mx-auto">
            {/* REMOVED: "European Industrial Transformation Investor" kicker */}

            {/* Main Title - No animation, static display */}
            <h1
              className="max-w-4xl mx-auto"
              style={{
                fontFamily: "'Bank Gothic', 'Impact', sans-serif",
                color: '#f4f5f7',
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '2rem'
              }}
            >
              {language === 'fr' ? "Capital et accompagnement de long terme pour les entreprises industrielles critiques en Europe." : "Long-term capital and hands-on partnership for critical industrial businesses across Europe."}
            </h1>

            {/* Subtitle with Fade-in Effect */}
            <p 
              style={{ 
                fontFamily: "'Eurostile', 'Arial', sans-serif", 
                color: '#f4f5f7', 
                fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', 
                lineHeight: 1.7, 
                maxWidth: '750px', 
                margin: '0 auto 2.5rem',
                opacity: heroVisible ? 0.9 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 1.2s ease-out 0.5s, transform 1.2s ease-out 0.5s'
              }}
            >
              {language === 'fr'
                ? "Investir et accompagner. En profondeur et dans la durée, aux côtés des dirigeants industriels."
                : "We invest and we operate. Deep inside the business and over the long run, alongside Europe's industrial leaders."}
            </p>

            <div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/approach" className="group inline-flex items-center justify-center gap-3 px-8 py-4 transition-all duration-500 hover:scale-105"
                  style={{ backgroundColor: '#2a6cae', fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#f4f5f7', fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                  {language === 'fr' ? 'Découvrir notre approche' : 'Discover our approach'}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/investments" className="group inline-flex items-center justify-center gap-3 px-8 py-4 border-2 transition-all duration-500 hover:scale-105 hover:bg-white/10"
                  style={{ borderColor: '#f4f5f7', fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#f4f5f7', fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                  {language === 'fr' ? 'Voir les investissements' : 'See our investments'}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ SCROLLING CONTENT CONTAINER ============ */}
      <div ref={scrollContentRef} className="relative z-10" style={{ marginTop: '100vh', backgroundColor: bgColor, boxShadow: '0 -20px 60px rgba(0,0,0,0.3)', transition: 'background-color 0.15s ease-out' }}>
      
      {/* ============ WHO WE ARE ============ */}
      <section className="py-10 lg:py-14 bg-transparent">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div 
              className="overflow-hidden group"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <img src="https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/lnbyeot2_image00003.jpeg" alt="Industrial" className="w-full h-[400px] lg:h-[500px] object-cover group-hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <div>
              <p className="mb-4" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: isLightBg ? '#2a6cae' : '#f4f5f7', fontSize: '14px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', transition: 'color 0.3s ease' }}>
                {language === 'fr' ? 'Qui sommes-nous' : 'Who we are'}
              </p>
              <h2 className="mb-6" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: textColor, fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 600, letterSpacing: '0.02em', transition: 'color 0.3s ease' }}>
                {language === 'fr' ? 'Construire des champions européens résilients' : 'Building Resilient European Industrial Champions'}
              </h2>
              <p className="mb-8" style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", color: textColor, fontSize: '18px', lineHeight: 1.8, opacity: isLightBg ? 0.7 : 0.85, transition: 'color 0.3s ease' }}>
                {language === 'fr' ? "Ocean Peak Group est né de la conviction que l'industrie européenne mérite des partenaires capables d'opérer là où la pression est élevée, avec maîtrise, humilité et engagement de long terme." : "Ocean Peak Group was founded on a simple conviction: European industry deserves partners willing to operate where the pressure is real, with rigour, humility, and a commitment that goes the distance."}
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 transition-all duration-300 hover:gap-4" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: isLightBg ? '#2a6cae' : '#f4f5f7', fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', transition: 'color 0.3s ease' }}>
                {language === 'fr' ? 'En savoir plus' : 'Learn more'} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ OUR INVESTMENTS - "governance" replaced with "operations" ============ */}
      <section className="py-10 lg:py-14 bg-transparent">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <p className="mb-4" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: isLightBg ? '#2a6cae' : '#f4f5f7', fontSize: '14px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', transition: 'color 0.3s ease' }}>
                {language === 'fr' ? 'Nos investissements' : 'Our investments'}
              </p>
              <h2 className="mb-6" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: textColor, fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 600, letterSpacing: '0.02em', transition: 'color 0.3s ease' }}>
                {language === 'fr' ? 'Un portefeuille concentré construit pour un partenariat actif' : 'A concentrated portfolio built for active partnership'}
              </h2>
              <p className="mb-4" style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", color: textColor, fontSize: '18px', lineHeight: 1.8, opacity: isLightBg ? 0.7 : 0.85, transition: 'color 0.3s ease' }}>
                {language === 'fr' ? "Nous investissons dans des entreprises industrielles européennes positionnées sur des marchés de niche stratégiques, dotées d'un savoir-faire technique reconnu et d'un potentiel de développement que seul un engagement au plus près du terrain peut révéler." : "We invest in European industrial companies positioned in strategic niche markets, with recognised technical expertise and growth potential that only deep, hands-on engagement can unlock."}
              </p>
              <p className="mb-8" style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", color: textColor, fontSize: '18px', lineHeight: 1.8, opacity: isLightBg ? 0.7 : 0.85, transition: 'color 0.3s ease' }}>
                {language === 'fr' ? "Notre approche repose sur la discipline d'investisseur et la profondeur d'analyse : nous sélectionnons des acteurs dont le savoir-faire industriel est authentiquement différenciant." : "Our approach is grounded in investor discipline and depth of analysis: we select businesses whose industrial know-how is genuinely differentiating."}
              </p>
              <Link to="/investments" className="inline-flex items-center gap-2 transition-all duration-300 hover:gap-4" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: isLightBg ? '#2a6cae' : '#f4f5f7', fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', transition: 'color 0.3s ease' }}>
                {language === 'fr' ? 'Voir les investissements' : 'View investments'}<ArrowRight size={14} />
              </Link>
            </div>
            <motion.div 
              className="order-1 lg:order-2 overflow-hidden group"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <img src="https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/j1c77g4l_VetterTec%20Workshop%20with%20Tube%20bundle%20dryers%20cmyk300.jpg" alt="Manufacturing" className="w-full h-[400px] lg:h-[500px] object-cover group-hover:scale-105 transition-transform duration-700" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ TEAM SECTION - Updated tagline ============ */}
      <section ref={teamSectionRef} className="py-10 lg:py-14 bg-transparent mb-4">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="grid grid-cols-3 gap-4">
              {teamMembers.map((member, idx) => (
                <motion.div 
                  key={idx} 
                  className="text-center group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  <div
                    className="mb-4 p-2 bg-white border-4 border-black transition-all duration-500 group-hover:shadow-2xl group-hover:-translate-y-1"
                    style={{
                      boxShadow: '8px 8px 0px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <div className="overflow-hidden">
                      <img src={member.image} alt={member.name} className="w-full aspect-[3/4] object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                    </div>
                  </div>
                  <h3 style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: textColor, fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '2px', transition: 'color 0.3s ease' }}>{member.name}</h3>
                  <p style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", color: textColor, fontSize: '10px', opacity: isLightBg ? 0.6 : 0.7, transition: 'color 0.3s ease' }}>{member.title}</p>
                </motion.div>
              ))}
            </div>
            <div>
              <p className="mb-4" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: isLightBg ? '#2a6cae' : '#f4f5f7', fontSize: '14px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', transition: 'color 0.3s ease' }}>
                {language === 'fr' ? 'Notre équipe' : 'Our team'}
              </p>
              <h2 className="mb-6" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: textColor, fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 600, letterSpacing: '0.02em', transition: 'color 0.3s ease' }}>
                {language === 'fr' ? "Expertise industrielle, expérience d'investissement, historique de transformation." : "Industrial expertise. Investment experience. A track record of transformation."}
              </h2>
              <p className="mb-8" style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", color: textColor, fontSize: '18px', lineHeight: 1.8, opacity: isLightBg ? 0.7 : 0.85, transition: 'color 0.3s ease' }}>
                {language === 'fr' ? "Ocean Peak Group réunit une équipe issue des mondes industriel et private equity. Nos profils partagent une même conviction : c'est au cœur des organisations, là où la pression est réelle et les décisions structurantes, que se joue la transformation durable de l'industrie." : "Ocean Peak Group brings together a team with roots in industry, private equity, and strategic consulting. We share a common belief: lasting industrial transformation happens at the core of organisations, where the pressure is real, and the decisions that matter most are made."}
              </p>
              <Link to="/team" className="inline-flex items-center gap-2 transition-all duration-300 hover:gap-4" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: isLightBg ? '#2a6cae' : '#f4f5f7', fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', transition: 'color 0.3s ease' }}>
                {language === 'fr' ? 'Rencontrer l\'équipe' : 'Meet the team'} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIAL CAROUSEL - "What we strive for" ============ */}
      <section
        ref={testimonialSectionRef}
        className="relative"
        style={{
          minHeight: '60vh',
          backgroundColor: isLightBg ? '#f4f5f7' : '#5b6770',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        {/* Section Header - Centered on top of carousel */}
        <div className="absolute top-0 left-0 right-0 z-10 pt-8">
          <p className="text-center" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#2a6cae', fontSize: '12px', fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            {language === 'fr' ? 'Ce que nous recherchons' : 'What we strive for'}
          </p>
        </div>

        {/* Crossfade Carousel - Centered quotes, no images */}
        <div className="relative w-full flex-1 flex items-center justify-center px-4 lg:px-16">
          {testimonials.map((testimonial, idx) => {
            const isActive = idx === currentTestimonial;

            return (
              <div
                key={idx}
                className="absolute inset-0 flex items-center justify-center px-4 lg:px-16"
                style={{
                  opacity: isActive ? 1 : 0,
                  transition: 'opacity 1s ease-in-out',
                  pointerEvents: isActive ? 'auto' : 'none'
                }}
              >
                <div className="flex flex-col items-center text-center max-w-[800px] mx-auto">
                  {/* Quote */}
                  <div className="mb-8">
                    <AnimatedQuote
                      text={testimonial.quote}
                      isVisible={isActive}
                      textColor={textColor}
                    />
                  </div>

                  {/* Horizontal Line */}
                  <div
                    className="h-px w-24 mb-6"
                    style={{
                      backgroundColor: textColor,
                      opacity: 0.3,
                      transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                      transition: 'transform 0.8s ease-out 0.5s'
                    }}
                  />

                  {/* Name and Title */}
                  <div
                    style={{
                      opacity: isActive ? 1 : 0,
                      transform: isActive ? 'translateY(0)' : 'translateY(10px)',
                      transition: 'opacity 0.6s ease-out 0.4s, transform 0.6s ease-out 0.4s'
                    }}
                  >
                    <h3 style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: textColor, fontSize: '16px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '4px' }}>
                      {testimonial.name}
                    </h3>
                    <p style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", color: textColor, fontSize: '14px', opacity: 0.7 }}>
                      {testimonial.title}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scroll Mouse Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="w-7 h-12 border-2 border-current rounded-full opacity-50 flex justify-center" style={{ color: textColor }}>
            <div 
              className="w-1.5 h-3 rounded-full mt-2"
              style={{ 
                backgroundColor: textColor,
                animation: 'fakeScroll 2s ease-in-out infinite'
              }}
            />
          </div>
        </div>
      </section>

      {/* ============ SECTORS OF FOCUS (renamed from Industrial Sectors) ============ */}
      <section className="py-16 lg:py-20" style={{ backgroundColor: isLightBg ? '#ffffff' : '#5b6770' }}>
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <h3 className="mb-6" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: textColor, fontSize: '1.5rem', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                {language === 'fr' ? "Secteurs d'intervention" : 'Key Sectors'}
              </h3>
              <p className="mb-8" style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", color: textColor, fontSize: '18px', lineHeight: 1.8, opacity: isLightBg ? 0.7 : 0.85 }}>
                {language === 'fr'
                  ? "Nous intervenons dans des secteurs essentiels à la résilience industrielle européenne, où la maîtrise technique, la profondeur des savoir-faire et la solidité des chaînes de valeur sont déterminantes."
                  : "We operate in sectors that are essential to European industrial resilience, where technical mastery, depth of expertise, and robust value chains make the difference."}
              </p>
              <Link to="/activities" className="inline-flex items-center gap-2 transition-all duration-300 hover:gap-4" style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: isLightBg ? '#2a6cae' : '#f4f5f7', fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                {language === 'fr' ? 'Voir les secteurs' : 'Explore our sectors'} <ArrowRight size={14} />
              </Link>
            </motion.div>
            
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_dac2dc4e-4f19-40ab-966a-0ff854a140af/artifacts/ddo5xhch_4.jpg"
                alt="Sectors of focus"
                className="w-full aspect-[4/3] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ METRICS/STATS SECTION - Updated numbers ============ */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#0a1f35' }}>
        <div className="max-w-[1800px] mx-auto">
          {/* Section Label */}
          <div className="text-center pt-10 pb-6">
            <p style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: 'rgba(244, 245, 247, 0.6)', fontSize: '12px', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              {language === 'fr' ? 'Métriques clés du portefeuille' : 'Key Portfolio Metrics'}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {metrics.map((metric, idx) => {
              const colorSets = [
                ['#2a6cae', '#0a1f35', '#5b6770'],
                ['#4fb785', '#2a6cae', '#0a1f35'],
                ['#5b6770', '#2a6cae', '#4fb785'],
                ['#2a6cae', '#4fb785', '#5b6770'],
                ['#0a1f35', '#2a6cae', '#4fb785'],
              ];
              
              return (
                <motion.div 
                  key={idx} 
                  className="relative overflow-hidden border-r border-b lg:border-b-0 border-white/10 last:border-r-0 group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <AnimatedGradient 
                    colors={colorSets[idx % colorSets.length]} 
                    speed={0.05} 
                    blur="medium" 
                  />
                  
                  <div className="relative z-10 p-8 lg:p-10 text-center backdrop-blur-sm">
                    <motion.div
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <metric.icon size={24} className="mx-auto mb-4 text-[#4fb785]" />
                    </motion.div>
                    <motion.p 
                      className="mb-2" 
                      style={{ fontFamily: "'Bank Gothic', 'Impact', sans-serif", color: '#f4f5f7', fontSize: '2rem', fontWeight: 700 }}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.5, delay: idx * 0.1 + 0.2 }}
                      viewport={{ once: true }}
                    >
                      {metric.value}
                    </motion.p>
                    <motion.p 
                      style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: 'rgba(244, 245, 247, 0.6)', fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase' }}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.5, delay: idx * 0.1 + 0.3 }}
                      viewport={{ once: true }}
                    >
                      {metric.label}
                    </motion.p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FOOTER CTA ============ */}
      <section className="py-12 lg:py-16 relative overflow-hidden" style={{ backgroundColor: '#0a1f35' }}>
        <div className="relative z-10 max-w-4xl mx-auto px-8 lg:px-12 text-center">
          {/* Animated Line */}
          <motion.div
            className="mx-auto mb-8"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
            style={{ 
              width: '120px', 
              height: '2px', 
              backgroundColor: '#2a6cae',
              transformOrigin: 'center'
            }}
          />
          
          {/* Two Buttons: Invest With Us & Partner With Us */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <Link to="/contact?type=invest" className="group inline-flex items-center justify-center gap-2 px-8 py-4 border-2 transition-all duration-500 hover:scale-105 hover:bg-white/10" style={{ borderColor: '#f4f5f7', fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#f4f5f7', fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              {language === 'fr' ? 'Investir avec nous' : 'Invest with us'}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact?type=work" className="group inline-flex items-center justify-center gap-2 px-8 py-4 border-2 transition-all duration-500 hover:scale-105 hover:bg-white/10" style={{ borderColor: '#f4f5f7', fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#f4f5f7', fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              {language === 'fr' ? 'Travailler avec nous' : 'Work with us'}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact?type=partner" className="group inline-flex items-center justify-center gap-2 px-8 py-4 border-2 transition-all duration-500 hover:scale-105 hover:bg-white/10" style={{ borderColor: '#f4f5f7', fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#f4f5f7', fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              {language === 'fr' ? 'Devenir partenaire' : 'Partner with us'}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
      </div>

    </div>
  );
};
